import { fabric } from 'fabric';
import { ANNOTATION_TYPE, DEFAULT_FONT_BOLD } from '@/shared/ui';

export type EventPositionOptions = {
  canvas: fabric.Canvas;
  offsetX: number;
  startPos?: number;
  stopPos?: number;
  config: any;
  isBeatPos: boolean;
  color?: string;
  id: string;
  event?: { type?: string; isHighLight?: boolean };
};

// eslint-disable-next-line import/no-mutable-exports
let EventPosition: any;

(function () {
  const FILL_COLOR: Record<string, string> = {
    red: 'rgba(242, 61, 66,0.25)',
    orange: 'rgba(255, 171, 0, 0.25)',
    gray: 'rgba(130, 140, 153, 0.25)',
  };

  const FILL_HIGHT_LIGHT_COLOR: Record<string, string> = {
    red: 'rgba(242, 61, 66, 1)',
    orange: 'rgba(235, 109, 23, 1)',
    gray: 'rgba(130, 140, 153, 1)',
  };

  const TEXT_FONT = {
    fill: '#F23D42',
    fontFamily: DEFAULT_FONT_BOLD,
    selectable: false,
    hasControls: false,
    hasBorders: false,
    hasRotatingPoint: false,
    selection: false,
    skipTargetFind: false,
    editable: false,
  };

  EventPosition = function ({
    canvas,
    offsetX,
    startPos = 0,
    stopPos = 0,
    config,
    isBeatPos,
    color,
    id,
    event,
  }: EventPositionOptions) {
    this.type = ANNOTATION_TYPE.EVENT_POSITION;
    this.canvas = canvas;
    this.config = config;
    this.xr = startPos;
    this.isBeatPos = isBeatPos;
    this.stopPos = stopPos;
    this.spp = (config.samplingRate * config.wndDuration) / config.width; // samplePerPixel
    this.x = this.xr > offsetX ? (this.xr - offsetX) / this.spp : this.xr / this.spp;
    this.xStop = (stopPos - this.xr) / this.spp;
    this.color = color;
    this.id = id;
    this.event = event;
    this.text = null;
  };

  EventPosition.prototype.draw = function () {
    const leftPos = this.isBeatPos
      ? this.x - this.config.samplingRate / 2 / 2 / this.spp
      : this.x + 1; // Dịch vị trí start event đi 1px để phân biệt những event liên tiếp nhau
    this.rect = new fabric.Rect({
      left: leftPos,
      top: 0,
      width: this.isBeatPos ? this.config.samplingRate / 2 / this.spp : this.xStop - 1, // Width trù đi 1px vì start + 1px
      height: this.config.height,
      fill: FILL_COLOR[this.color || 'red'],
      selectable: false,
      hasBorders: false,
      hoverCursor: 'arrow',
    });
    if (this.event?.type) {
      this.text = new fabric.Text(this.event.type, {
        ...TEXT_FONT,
        fontWeight: 400,
        fontSize: 14,
        left: leftPos + 5,
        top: this.config.height - 22,
        hoverCursor: 'arrow',
      });
      this.canvas.add(this.text);
    }
    if (this.event?.isHighLight) {
      this.bottomRect = new fabric.Rect({
        left: leftPos,
        top: this.config.height - 4,
        width: this.isBeatPos ? this.config.samplingRate / 2 / this.spp : this.xStop - 1, // Width trù đi 1px vì start + 1px
        height: 4,
        fill: FILL_HIGHT_LIGHT_COLOR[this.color || 'red'],
        selectable: false,
        hasBorders: false,
        hoverCursor: 'arrow',
      });
      this.bottomRect.id = 'bottomRect';
      this.canvas.add(this.bottomRect);
    }

    this.rect.id = 'rect';
    this.canvas.add(this.rect);
  };

  EventPosition.prototype.render = function () {
    this.canvas.renderAll();
  };

  EventPosition.prototype.update = function (delta: any) {
    // Can not modify this object
  };

  EventPosition.prototype.updateConfig = function (config: any, offsetX: number) {
    this.config = config;
    this.spp = (config.samplingRate * config.wndDuration) / config.width;
    this.x = (this.xr - offsetX) / this.spp;
    this.xStop = (this.stopPos - this.xr) / this.spp;
    const leftPos = this.isBeatPos
      ? this.x - this.config.samplingRate / 2 / 2 / this.spp
      : this.x + 1;
    this.rect.set({
      left: leftPos,
      width: this.isBeatPos ? this.config.samplingRate / 2 / this.spp : this.xStop - 1,
      height: this.config.height,
    });
    this.rect.setCoords();
    if (this.event?.isHighLight) {
      this.bottomRect.set({
        left: leftPos,
        width: this.isBeatPos ? this.config.samplingRate / 2 / this.spp : this.xStop - 1,
        height: this.config.height,
      });
      this.bottomRect.setCoords();
    }

    if (this.event?.type) {
      this.text.set({
        left: leftPos + 5,
      });
      this.text.setCoords();
    }
  };

  EventPosition.prototype.scroll = function (offsetX: number) {
    this.x = (this.xr - offsetX) / this.spp;
    const leftPos = this.isBeatPos
      ? this.x - this.config.samplingRate / 2 / 2 / this.spp
      : this.x + 1;
    this.rect.set({ left: leftPos });
    this.rect.setCoords();
    if (this.event?.isHighLight) {
      this.bottomRect.set({ left: leftPos });
      this.bottomRect.setCoords();
    }
    if (this.event?.type) {
      this.text.set({
        left: leftPos + 5,
      });
      this.text.setCoords();
    }
  };

  EventPosition.prototype.isSelected = function (pointer: any) {
    return false; // Can not select this annotation
  };

  EventPosition.prototype.select = function () {
    // Can not select this annotation
  };

  EventPosition.prototype.deselect = function () {
    // Can not deselect this annotation
  };

  EventPosition.prototype.isVisibility = function (isVisibility: boolean) {
    this.rect.visible = isVisibility;
    if (this.event?.type) {
      this.text.visible = isVisibility;
    }
    if (this.event?.isHighLight) {
      this.bottomRect.visible = isVisibility;
    }
  };

  EventPosition.prototype.getJSONObject = function () {
    return {
      type: this.type,
      position: this.xr,
    };
  };

  EventPosition.prototype.import = function (data: any) {
    // This object import by create instance
  };
})();

export { EventPosition };
export default { EventPosition };
